import { getAuthHeaders } from "../lib/auth";
import { useState, useEffect, useCallback, useRef } from "react";
import LogLayout from "../components/LogLayout";
import {
  Search, Filter, FileText, ChevronLeft, ChevronRight, Loader2, Eye,
  Upload, File, Image, Download, Trash2, FolderOpen, HardDrive, X, Plus, Paperclip
} from "lucide-react";
import { Button } from "../components/ui/button";
import AttachToInvoiceModal from "../components/AttachToInvoiceModal";
import { toast } from "sonner";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const getHeaders = getAuthHeaders;
const formatFCFA = (n) => new Intl.NumberFormat("fr-FR").format(Math.round(n || 0)) + " FCFA";
const formatSize = (bytes) => {
  if (!bytes) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
};

const INVOICE_STATUSES = [
  { value: "received", label: "Recue" }, { value: "deposited", label: "Déposée" },
  { value: "in_recovery", label: "En recouvrement" }, { value: "partially_paid", label: "Paiement partiel" },
  { value: "paid", label: "Payée" }, { value: "archived", label: "Archivée" },
];
const getStatusLabel = (s) => INVOICE_STATUSES.find(st => st.value === s)?.label || s;
const getStatusColor = (s) => {
  const m = { received: "bg-blue-500/100/15 text-blue-400", deposited: "bg-cyan-500/100/15 text-cyan-400", in_recovery: "bg-yellow-500/15 text-yellow-400", partially_paid: "bg-orange-500/15 text-orange-400", paid: "bg-emerald-500/15 text-emerald-400", archived: "bg-white/50/15 text-slate-400" };
  return m[s] || "bg-white/50/15 text-slate-400";
};

const DOC_CATEGORIES = [
  { value: "general", label: "General" },
  { value: "facture", label: "Facture" },
  { value: "cheque", label: "Chèque" },
  { value: "remise", label: "Remise bancaire" },
  { value: "ordonnance", label: "Ordonnance" },
  { value: "bon_commande", label: "Bon de commande" },
  { value: "bon_livraison", label: "Bon de livraison" },
  { value: "tracabilite", label: "Tracabilite" },
  { value: "attestation", label: "Attestation" },
  { value: "contrat", label: "Contrat" },
  { value: "autre", label: "Autre" },
];
const EXT_ICONS = { pdf: "text-red-400", doc: "text-blue-400", docx: "text-blue-400", xls: "text-green-400", xlsx: "text-green-400", jpg: "text-amber-400", jpeg: "text-amber-400", png: "text-amber-400" };

export default function LogArchivesPage() {
  const [activeTab, setActiveTab] = useState("invoices");

  return (
    <LogLayout title="Documents archivés" subtitle="Retrouvez tous vos dossiers et documents archivés">
      <div data-testid="log-archives-page" className="space-y-6">
        <div className="flex gap-1 bg-slate-900/60 p-1 rounded-xl border border-white/5 w-fit">
          <button onClick={() => setActiveTab("invoices")} data-testid="archives-tab-invoices"
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-all ${activeTab === "invoices" ? "bg-blue-600 text-white font-medium" : "text-slate-400 hover:text-white hover:bg-white/5"}`}>
            <FolderOpen className="w-4 h-4" /> Dossiers Factures
          </button>
          <button onClick={() => setActiveTab("documents")} data-testid="archives-tab-documents"
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-all ${activeTab === "documents" ? "bg-blue-600 text-white font-medium" : "text-slate-400 hover:text-white hover:bg-white/5"}`}>
            <HardDrive className="w-4 h-4" /> Documents & Tracabilite
          </button>
        </div>
        {activeTab === "invoices" ? <InvoiceArchives /> : <DocumentArchives />}
      </div>
    </LogLayout>
  );
}

function InvoiceArchives() {
  const [results, setResults] = useState({ invoices: [], total: 0, page: 1, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [insurers, setInsurers] = useState([]);
  const [insurerId, setInsurerId] = useState("");
  const navigate = useNavigate();

  useEffect(() => { axios.get(`${API}/log/invoices/insurers`, { headers: getHeaders() }).then(r => setInsurers(r.data.insurers || [])).catch(() => {}); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (search) params.search = search;
      if (status) params.status = status;
      if (insurerId) params.insurer_id = insurerId;
      const res = await axios.get(`${API}/log/compliance/archives`, { headers: getHeaders(), params });
      setResults(res.data);
    } catch (e) { toast.error("Erreur chargement"); }
    finally { setLoading(false); }
  }, [search, status, insurerId, page]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="invoice-archives-section">
      <div className="bg-slate-900 border border-white/10 rounded-xl p-4">
        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder="Rechercher..." className="w-full pl-9 pr-3 py-2 bg-slate-950 border border-white/10 rounded-lg text-white placeholder:text-slate-500 focus:border-blue-500 focus:outline-none text-sm" data-testid="archives-search" />
          </div>
          <select value={status} onChange={e => { setStatus(e.target.value); setPage(1); }} className="bg-slate-950 border border-white/10 rounded-lg text-slate-300 px-3 py-2 text-sm" data-testid="archives-filter-status">
            <option value="">Tous les statuts</option>
            {INVOICE_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <select value={insurerId} onChange={e => { setInsurerId(e.target.value); setPage(1); }} className="bg-slate-950 border border-white/10 rounded-lg text-slate-300 px-3 py-2 text-sm">
            <option value="">Tous les assureurs</option>
            {insurers.map(ins => <option key={ins.id} value={ins.id}>{ins.name}</option>)}
          </select>
        </div>
      </div>

      <div className="bg-slate-900 border border-white/10 rounded-xl overflow-hidden">
        <div className="px-5 py-3 border-b border-white/5 flex items-center justify-between">
          <p className="text-sm text-slate-300">{results.total} dossier(s)</p>
          <p className="text-xs text-slate-500">Page {results.page} / {results.pages}</p>
        </div>
        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-blue-400" /></div>
        ) : results.invoices.length === 0 ? (
          <p className="text-slate-500 text-center py-10">Aucun dossier</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead><tr className="border-b border-white/5 text-slate-500 text-xs uppercase">
                <th className="text-left px-4 py-3">N° Facture</th><th className="text-left px-4 py-3">Assureur</th>
                <th className="text-right px-4 py-3">Montant</th><th className="text-center px-4 py-3">Statut</th>
                <th className="text-center px-4 py-3">Docs</th><th className="text-center px-4 py-3">Action</th>
              </tr></thead>
              <tbody>
                {results.invoices.map(inv => (
                  <tr key={inv.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                    <td className="px-4 py-3 text-white font-medium">{inv.invoice_number}</td>
                    <td className="px-4 py-3 text-slate-300">{inv.insurer_name}</td>
                    <td className="px-4 py-3 text-right text-white">{formatFCFA(inv.amount)}</td>
                    <td className="px-4 py-3 text-center"><span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(inv.status)}`}>{getStatusLabel(inv.status)}</span></td>
                    <td className="px-4 py-3 text-center text-slate-400">{(inv.photos || []).length}</td>
                    <td className="px-4 py-3 text-center">
                      <button onClick={() => navigate(`/log/invoices?open=${inv.id}`)} className="p-1.5 rounded-lg hover:bg-white/5 text-blue-400"><Eye className="w-4 h-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {results.pages > 1 && (
          <div className="flex items-center justify-center gap-2 py-3 border-t border-white/5">
            <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="text-slate-400"><ChevronLeft className="w-4 h-4" /></Button>
            <span className="text-xs text-slate-400">{page} / {results.pages}</span>
            <Button variant="ghost" size="sm" disabled={page >= results.pages} onClick={() => setPage(p => p + 1)} className="text-slate-400"><ChevronRight className="w-4 h-4" /></Button>
          </div>
        )}
      </div>
    </div>
  );
}

function DocumentArchives() {
  const [docs, setDocs] = useState({ documents: [], total: 0, page: 1, pages: 1 });
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [page, setPage] = useState(1);
  const [uploading, setUploading] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [uploadCat, setUploadCat] = useState("general");
  const [uploadDesc, setUploadDesc] = useState("");
  const [viewImg, setViewImg] = useState(null);
  const [imgBlobUrl, setImgBlobUrl] = useState(null);
  const [attachTarget, setAttachTarget] = useState(null); // { docs: [doc], defaultCategory } when ouvert
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (search) params.search = search;
      if (category) params.category = category;
      const [docsRes, statsRes] = await Promise.all([
        axios.get(`${API}/log/documents`, { headers: getHeaders(), params }),
        axios.get(`${API}/log/documents/stats`, { headers: getHeaders() }),
      ]);
      setDocs(docsRes.data);
      setStats(statsRes.data);
    } catch { }
    finally { setLoading(false); }
  }, [search, category, page]);

  useEffect(() => { load(); }, [load]);

  const uploadDoc = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      if (files.length === 1) {
        // Backward compat : single file
        const fd = new FormData();
        fd.append("file", files[0]);
        fd.append("category", uploadCat);
        fd.append("description", uploadDesc || files[0].name);
        await axios.post(`${API}/log/documents/upload`, fd, { headers: { ...getHeaders(), "Content-Type": "multipart/form-data" } });
        toast.success("Document archive !");
      } else {
        // Multi-upload
        const fd = new FormData();
        files.forEach((f) => fd.append("files", f));
        fd.append("category", uploadCat);
        fd.append("description", uploadDesc || "");
        const res = await axios.post(`${API}/log/documents/upload-multiple`, fd, { headers: { ...getHeaders(), "Content-Type": "multipart/form-data" } });
        const created = res.data.total_created || 0;
        const errs = res.data.total_errors || 0;
        if (errs === 0) toast.success(`${created} document(s) archive(s) avec succes !`);
        else toast.warning(`${created} archive(s), ${errs} erreur(s)`);
      }
      setShowUpload(false);
      setUploadDesc("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur upload");
    } finally {
      setUploading(false);
      if (e?.target) e.target.value = "";
    }
  };

  const downloadDoc = async (doc) => {
    try {
      const res = await axios.get(`${API}/log/documents/${doc.id}/download`, { headers: getHeaders(), responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = doc.original_filename; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Erreur téléchargément"); }
  };

  const viewImage = async (doc) => {
    try {
      const res = await axios.get(`${API}/log/documents/${doc.id}/download`, { headers: getHeaders(), responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: doc.content_type }));
      setImgBlobUrl(url);
      setViewImg(doc);
    } catch (e) { toast.error("Erreur chargement image"); }
  };

  const closeViewer = () => {
    if (imgBlobUrl) window.URL.revokeObjectURL(imgBlobUrl);
    setViewImg(null);
    setImgBlobUrl(null);
  };

  const deleteDoc = async (id) => {
    if (!window.confirm("Supprimer ce document ?")) return;
    try {
      await axios.delete(`${API}/log/documents/${id}`, { headers: getHeaders() });
      toast.success("Document supprimé");
      load();
    } catch (e) { toast.error("Erreur"); }
  };

  return (
    <div className="space-y-4" data-testid="documents-archives-section">
      {/* Bandeau d'aide : flux Archives → Dossiers Factures */}
      <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-xl p-4 flex items-start gap-3">
        <div className="w-8 h-8 rounded-lg bg-emerald-500/15 flex items-center justify-center flex-shrink-0">
          <Paperclip className="w-4 h-4 text-emerald-400" />
        </div>
        <div className="flex-1">
          <p className="text-xs font-semibold text-emerald-300 uppercase tracking-wider">Comment utiliser ces archives ?</p>
          <p className="text-xs text-slate-400 mt-1">
            Scannez ici tous vos documents en vrac (factures, chèques, remises, bons…). Quand vous créez ou complétez un <span className="text-white font-medium">dossier facture</span>, cliquez sur l'icône <Paperclip className="w-3 h-3 inline text-emerald-400" /> à droite d'un document pour le <span className="text-emerald-400 font-medium">rattacher comme pièce justificative</span>.
          </p>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="bg-blue-500/100/10 border border-blue-500/20 rounded-xl p-4">
            <p className="text-2xl font-bold text-white">{stats.total}</p>
            <p className="text-[10px] text-slate-500 uppercase">Total documents</p>
          </div>
          {Object.entries(stats.by_category || {}).slice(0, 3).map(([cat, data]) => (
            <div key={cat} className="bg-slate-900 border border-white/5 rounded-xl p-4">
              <p className="text-lg font-bold text-white">{data.count}</p>
              <p className="text-[10px] text-slate-500 uppercase">{DOC_CATEGORIES.find(c => c.value === cat)?.label || cat} ({formatSize(data.size)})</p>
            </div>
          ))}
        </div>
      )}

      {/* Filters + Upload */}
      <div className="bg-slate-900 border border-white/10 rounded-xl p-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder="Rechercher un document..." className="w-full pl-9 pr-3 py-2 bg-slate-950 border border-white/10 rounded-lg text-white placeholder:text-slate-500 focus:border-blue-500 focus:outline-none text-sm" data-testid="docs-search" />
        </div>
        <select value={category} onChange={e => { setCategory(e.target.value); setPage(1); }} className="bg-slate-950 border border-white/10 rounded-lg text-slate-300 px-3 py-2 text-sm" data-testid="docs-filter-category">
          <option value="">Toutes categories</option>
          {DOC_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
        <Button onClick={() => setShowUpload(!showUpload)} className="bg-blue-600 hover:bg-blue-500/100 text-white gap-2" data-testid="docs-upload-btn">
          <Plus className="w-4 h-4" /> Archiver un document
        </Button>
      </div>

      {/* Upload form */}
      {showUpload && (
        <div className="bg-blue-500/100/5 border border-blue-500/20 rounded-xl p-5 space-y-3">
          <h3 className="text-sm font-bold text-blue-400 flex items-center gap-2"><Upload className="w-4 h-4" /> Archiver un nouveau document</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <select value={uploadCat} onChange={e => setUploadCat(e.target.value)} data-testid="upload-category"
              className="bg-slate-900/60 border border-white/10 rounded-lg text-slate-300 px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none">
              {DOC_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
            <input value={uploadDesc} onChange={e => setUploadDesc(e.target.value)} placeholder="Description du document..."
              className="bg-slate-900/60 border border-white/10 rounded-lg text-white px-3 py-2.5 text-sm placeholder:text-slate-500 focus:border-blue-500 focus:outline-none" data-testid="upload-desc" />
            <div>
              <input ref={fileRef} type="file" multiple onChange={uploadDoc} className="hidden" accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.jpg,.jpeg,.png,.gif,.webp" />
              <Button onClick={() => fileRef.current?.click()} disabled={uploading} className="w-full bg-blue-600 hover:bg-blue-500/100 text-white gap-2 h-10" data-testid="upload-file-btn">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {uploading ? "Envoi..." : "Choisir un ou plusieurs fichiers"}
              </Button>
            </div>
          </div>
          <p className="text-[10px] text-slate-500">Formats acceptes: PDF, Word, Excel, CSV, Images (JPG, PNG) — Max 20 MB par fichier — Jusqu'a 50 fichiers en une fois</p>
        </div>
      )}

      {/* Full-screen Image Viewer */}
      {viewImg && imgBlobUrl && (
        <div className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4" onClick={closeViewer} data-testid="archive-image-viewer">
          <div className="absolute top-4 right-4 flex items-center gap-2 z-10">
            <button onClick={(e) => { e.stopPropagation(); downloadDoc(viewImg); }} className="text-white bg-sky-500/80 hover:bg-sky-500 p-2 rounded-full" title="Telecharger">
              <Download className="w-5 h-5" />
            </button>
            <button onClick={closeViewer} className="text-white bg-white/10 hover:bg-white/20 p-2 rounded-full">
              <X className="w-6 h-6" />
            </button>
          </div>
          <div className="max-w-[90vw] max-h-[90vh] overflow-auto" onClick={e => e.stopPropagation()}>
            <img src={imgBlobUrl} alt={viewImg.original_filename} className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl" />
            <p className="text-center text-xs text-slate-500 mt-2">{viewImg.original_filename} — {DOC_CATEGORIES.find(c => c.value === viewImg.category)?.label || viewImg.category} — {formatSize(viewImg.size)}</p>
          </div>
        </div>
      )}

      {/* Documents list */}
      <div className="bg-slate-900 border border-white/10 rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-blue-400" /></div>
        ) : docs.documents.length === 0 ? (
          <p className="text-slate-500 text-center py-10">Aucun document archive</p>
        ) : (
          <div className="divide-y divide-white/5">
            {docs.documents.map(doc => {
              const extColor = EXT_ICONS[doc.extension] || "text-slate-400";
              const isImage = doc.content_type?.startsWith("image/");
              return (
                <div key={doc.id} data-testid={`doc-item-${doc.id}`} className="flex items-center gap-4 px-5 py-3 hover:bg-white/[0.02]">
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 bg-slate-800">
                    {isImage ? <Image className={`w-4 h-4 ${extColor}`} /> : <File className={`w-4 h-4 ${extColor}`} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate">{doc.original_filename}</p>
                    <div className="flex items-center gap-3 text-[10px] text-slate-500 mt-0.5">
                      <span className="bg-slate-800 px-1.5 py-0.5 rounded">{DOC_CATEGORIES.find(c => c.value === doc.category)?.label || doc.category}</span>
                      <span>{formatSize(doc.size)}</span>
                      <span>{new Date(doc.created_at).toLocaleDateString("fr-FR")}</span>
                      {doc.uploaded_by_name && <span>{doc.uploaded_by_name}</span>}
                      {doc.description && doc.description !== doc.original_filename && <span className="text-slate-600 truncate max-w-[200px]">{doc.description}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {isImage && (
                      <button onClick={() => viewImage(doc)} className="p-1.5 rounded-lg hover:bg-white/5 text-emerald-400" title="Voir en grand" data-testid={`view-doc-${doc.id}`}>
                        <Eye className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      onClick={() => setAttachTarget({ docs: [doc], defaultCategory: doc.category === "general" ? "facture" : doc.category })}
                      className="p-1.5 rounded-lg hover:bg-emerald-500/10 text-emerald-400"
                      title="Rattacher à un dossier facture"
                      data-testid={`attach-doc-${doc.id}`}
                    >
                      <Paperclip className="w-4 h-4" />
                    </button>
                    <button onClick={() => downloadDoc(doc)} className="p-1.5 rounded-lg hover:bg-white/5 text-blue-400" data-testid={`download-doc-${doc.id}`}>
                      <Download className="w-4 h-4" />
                    </button>
                    <button onClick={() => deleteDoc(doc.id)} className="p-1.5 rounded-lg hover:bg-white/5 text-red-400" data-testid={`delete-doc-${doc.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {docs.pages > 1 && (
          <div className="flex items-center justify-center gap-2 py-3 border-t border-white/5">
            <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="text-slate-400"><ChevronLeft className="w-4 h-4" /></Button>
            <span className="text-xs text-slate-400">{page} / {docs.pages}</span>
            <Button variant="ghost" size="sm" disabled={page >= docs.pages} onClick={() => setPage(p => p + 1)} className="text-slate-400"><ChevronRight className="w-4 h-4" /></Button>
          </div>
        )}
      </div>

      {/* Modal de rattachement à un dossier facture */}
      {attachTarget && (
        <AttachToInvoiceModal
          documentIds={attachTarget.docs.map(d => d.id)}
          defaultCategory={attachTarget.defaultCategory}
          onClose={() => setAttachTarget(null)}
          onAttached={() => { setAttachTarget(null); }}
        />
      )}
    </div>
  );
}
